import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDate, fmtMoney, PR_STATUS } from "../../lib/labels";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function NewPrDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({ description: "", justification: "", estimated_amount: "", needed_by: "", budget_reference: "", project: "" });
  const [busy, setBusy] = useState(false);
  const { data: budget } = useQuery({ queryKey: ["fin-budget-refs"], queryFn: async () => (await api.get("/finanzas/budget")).data, staleTime: 60_000 });

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finanzas/purchase-requests", { ...form, estimated_amount: form.estimated_amount ? Number(form.estimated_amount) : undefined });
      toast.success(`Solicitud ${r.data.pr_number} creada en borrador`);
      onDone(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-pr-dialog">
        <DialogHeader><DialogTitle>Nueva solicitud de compra</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Descripción de la necesidad *</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pr-description" /></div>
          <div><Label>Justificación</Label><Textarea value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} data-testid="pr-justification" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Importe estimado (€)</Label><Input type="number" value={form.estimated_amount} onChange={(e) => setForm({ ...form, estimated_amount: e.target.value })} data-testid="pr-amount" /></div>
            <div><Label>Fecha necesaria</Label><Input type="date" value={form.needed_by} onChange={(e) => setForm({ ...form, needed_by: e.target.value })} data-testid="pr-needed-by" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Partida presupuestaria</Label>
              <Select value={form.budget_reference} onValueChange={(v) => setForm({ ...form, budget_reference: v })}>
                <SelectTrigger data-testid="pr-budget-ref"><SelectValue placeholder="Selecciona partida…" /></SelectTrigger>
                <SelectContent>{(budget?.references || []).map((r) => <SelectItem key={r.code} value={r.code}>{r.code} — {r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Proyecto</Label><Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} data-testid="pr-project" /></div>
          </div>
          <Button className="w-full" onClick={submit} disabled={busy || !form.description.trim()} data-testid="pr-submit">
            {busy ? "Creando…" : "Crear solicitud"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Solicitudes() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data: items } = useQuery({
    queryKey: ["fin-prs", status, q],
    queryFn: async () => (await api.get("/finanzas/purchase-requests", { params: { status: status || undefined, q: q || undefined } })).data,
  });
  const highlight = params.get("pr");

  return (
    <div data-testid="solicitudes-page">
      <PageHeader
        title="Solicitudes de compra"
        subtitle="De la necesidad a la orden: ofertas, comparación, presupuesto y aprobación por matriz de importe."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-pr-button"><Plus className="mr-1 h-4 w-4" /> Nueva solicitud</Button>}
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar por descripción, número, proveedor…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="pr-search" />
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="pr-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(PR_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items && items.length === 0 && <EmptyState title="Sin solicitudes" description="Crea la primera solicitud de compra." />}
      {items?.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Solicitud</TableHead><TableHead>Proyecto</TableHead><TableHead>Partida</TableHead>
                <TableHead>Necesaria</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Importe est.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((pr) => (
                <TableRow key={pr.id} data-testid={`pr-row-${pr.id}`} className={`cursor-pointer ${highlight === pr.id ? "bg-blue-50/60" : ""}`} onClick={() => navigate(`/finanzas/solicitudes/${pr.id}`)}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{pr.description}</p>
                    <p className="font-mono text-xs text-slate-400">{pr.pr_number} · {(pr.quote_ids || []).length} ofertas{pr.comparison_id ? " · comparada" : ""}</p>
                  </TableCell>
                  <TableCell className="text-sm">{pr.project || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{pr.budget_reference || "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDate(pr.needed_by)}</TableCell>
                  <TableCell><Chip map={PR_STATUS} value={pr.status} testid={`pr-status-${pr.id}`} /></TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(pr.estimated_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewPrDialog open={open} onClose={() => setOpen(false)} onDone={(pr) => { setOpen(false); qc.invalidateQueries({ queryKey: ["fin-prs"] }); navigate(`/finanzas/solicitudes/${pr.id}`); }} />
      {highlight && <Link to={`/finanzas/solicitudes/${highlight}`} className="mt-3 inline-block text-sm font-medium text-blue-700 hover:underline" data-testid="pr-highlight-link">Abrir la solicitud del escenario →</Link>}
    </div>
  );
}
